import asbestosProjectService from "@/views/asbestosProjects/asbestosProjectService";
import { apiService, type PagingApiAndNotifierConfig, type Paging } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import { ToastOptionsSuccess, ToastOptionsError } from "@modernary/mui/components/toasts";
import { apiHosts } from "@/api/constants/apiHosts";

import type { AsbestosProject, AsbestosProjectNew, AsbestosProjectQuery } from "./asbestosProject";
import type { AxiosResponse } from "axios";
import { LE } from "@/constants/enums";

const URL = "asbestos-projects";
const ENTITY_NAME = "Asbestos Project";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_AQMS_API_BASE_URL,
};
const api = apiService<AsbestosProject>(apiHosts.AQMS, axiosParams);

const getAsbestosProjects = (params: AsbestosProjectQuery, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successToastCb,
    errorToastCb,
  });
};

const getAsbestosProjectById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successToastCb,
    errorToastCb,
  });
};

const getNewAsbestosProject = async (ids: AsbestosProjectIds) => {
  const defaultApp = {
    data: {
      ...asbestosProjectService.getInitialData(),
      ...ids,
    },
  } as AxiosResponse<AsbestosProjectNew, any>;

  return defaultApp;
};

type AsbestosProjectIds = {
  id?: string | number;
};
const getAsbestosProjectByIdOrNew = (ids: AsbestosProjectIds, config: PagingApiAndNotifierConfig = {}) => {
  if (!ids.id) {
    return getNewAsbestosProject(ids);
  } else {
    return getAsbestosProjectById(ids.id, config) as Promise<AxiosResponse<AsbestosProjectNew, any>>;
  }
};

const createAsbestosProject = (asbestosProject: AsbestosProject, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.post(URL, asbestosProject, {
    successToastCb,
    errorToastCb,
  });
};

const updateAsbestosProject = (asbestosProject: AsbestosProject, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.put(URL + "/" + asbestosProject.id, asbestosProject, {
    successToastCb,
    errorToastCb,
  });
};

/**
 * @param {AsbestosProject} asbestosProject This cannot be a 'ref' to a asbestosProject
 */
const saveAsbestosProject = (asbestosProject: AsbestosProject, config = {}) => {
  if (asbestosProject.id) {
    return updateAsbestosProject(asbestosProject, config);
  } else {
    return createAsbestosProject(asbestosProject, config);
  }
};

const deleteAsbestosProjectById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.delete(URL + "/" + id, {
    successToastCb,
    errorToastCb,
  });
};

export {
  deleteAsbestosProjectById,
  getAsbestosProjects,
  getAsbestosProjectById,
  getNewAsbestosProject,
  getAsbestosProjectByIdOrNew,
  createAsbestosProject,
  updateAsbestosProject,
  saveAsbestosProject,
};
